import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';
import useApi from 'hooks/useApi';
import { TagFormWrapper } from './styles';
import Form from './Form';

const TagForm = ({ onCancel, currentTag, refreshTagsData }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const auth = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [showError, setShowError] = useState(false);
  const [{ loading: loadingCreate }, createTag] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${auth.cusNavId}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingUpdate }, updateTag] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onCreateTag = async (values) => {
    try {
      const newData = [
        {
          I18List: [
            {
              I18Code: selectedDefaultLanguage,
              I18Name: values.tagName,
            },
          ],
        },
      ];
      const {
        data: { dataErrors },
      } = await createTag({ data: newData });
      refreshTagsData();
      onCancel();
      if (dataErrors.length) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
    }
  };

  const onEditTag = async (values) => {
    try {
      const { clientNavisionId, id, i18List } = currentTag;
      const oldI18List = i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const newData = [
        {
          id,
          I18List: [
            ...oldI18List,
            {
              I18Code: selectedDefaultLanguage,
              I18Name: values.tagName,
            },
          ],
        },
      ];
      const {
        data: { dataErrors },
      } = await updateTag({
        url: `${window.API_V1}/cus/tags/${clientNavisionId}/modify`,
        data: newData,
      });
      refreshTagsData();
      onCancel();
      if (dataErrors.length) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
    }
  };

  const onAddTag = (values) => {
    if (currentTag) {
      onEditTag(values);
    } else {
      onCreateTag(values);
    }
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{currentTag ? t('tag_text_edit') : t('tag_text_new')}</h3>
        </Col>
      </Row>
    );
  };
  return (
    <TagFormWrapper>
      <Title />
      <Form onCancel={onCancel} onSubmit={onAddTag} defaultValue={currentTag} />
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('custom_field_operation_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      {(loadingCreate || loadingUpdate) && <MainSpinner />}
    </TagFormWrapper>
  );
};

TagForm.defaultProps = {
  currentTag: null,
};

TagForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  refreshTagsData: PropTypes.func.isRequired,
  currentTag: PropTypes.shape({
    name: PropTypes.string,
    clientNavisionId: PropTypes.string,
    id: PropTypes.string,
    i18List: PropTypes.arrayOf(
      PropTypes.shape({
        i18Code: PropTypes.string,
      })
    ),
  }),
};

export default TagForm;
